import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./OnboardingVolunteer.module.scss";
import { useTranslation } from "../../i18n";
import { Button, CircularProgress } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { fetcher } from "../../utils/fetcher";
import { MunicipalitySuggestion } from "../InputMunicipality/InputMunicipality";
import { AuthContext } from "../../context/AuthContext";
import { OnboardingVolunteerFirstStep } from "./OnboardingVolunteerFirstStep";
import { OnboardingVolunteerThirdStep } from "./OnboardingVolunteerThirdStep";
import { OnboardingVolunteerSecondStep } from "./OnboardingVolunteerSecondStep";
import { DuplicateEmailNotice } from "../DuplicateEmailNotice/DuplicateEmailNotice";

interface Step {
  step: number;
  titleKey: string;
  descKey: string;
}

const steps: Step[] = [
  {
    step: 0,
    titleKey: "orgProfile.basicInfo",
    descKey: "onboarding.volStep1Desc",
  },
  {
    step: 1,
    titleKey: "volunteer.skills",
    descKey: "onboarding.volSkillsDesc",
  },
  {
    step: 2,
    titleKey: "volunteer.interests",
    descKey: "onboarding.volInterestsDesc",
  },
];

type FormData = {
  skills: string[];
  interests: string[];
  locations: MunicipalitySuggestion[];
  [key: string]: any;
};

export const OnboardingVolunteer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error("AuthContext must be used within an AuthProvider");
  }

  const { currentUser, loading } = authContext;

  const [selectedTowns, setSelectedTowns] = useState<MunicipalitySuggestion[]>(
    [],
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    skills: [],
    interests: [],
    locations: selectedTowns,
  });
  const [error, setError] = useState<string | false>(false);
  const [duplicateEmail, setDuplicateEmail] = useState(false);

  useEffect(() => {
    if (currentUser) {
      const userData = currentUser.userData as any;
      const locations: MunicipalitySuggestion[] = Array.isArray(
        userData?.locations,
      )
        ? userData.locations.filter(
            (l: any) => l && typeof l === "object" && l.name,
          )
        : [];
      setCurrentStep(userData?.step_of_registration || 0);
      setFormData({
        skills: (userData?.skills || []).map((id: number) => String(id)),
        interests: (userData?.interests || []).map((id: number) => String(id)),
        locations,
      });
      setSelectedTowns(locations);
    }
  }, [currentUser]);

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      locations: selectedTowns,
    }));
  }, [selectedTowns]);

  const handleCheckboxToggle = (
    value: string,
    field: "skills" | "interests",
  ) => {
    const strValue = String(value);
    const alreadyExists = formData[field]?.includes(strValue);
    if (alreadyExists) {
      setFormData({
        ...formData,
        [field]: formData[field].filter((item) => item !== strValue),
      });
    } else {
      setFormData({
        ...formData,
        [field]: [...formData[field], strValue],
      });
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <OnboardingVolunteerFirstStep
            title={t(steps[currentStep]?.titleKey ?? "")}
            desc={t(steps[currentStep]?.descKey ?? "")}
            onSetError={setError}
            onDuplicateEmail={() => setDuplicateEmail(true)}
          />
        );
      case 1:
        return (
          <OnboardingVolunteerSecondStep
            title={t(steps[currentStep]?.titleKey ?? "")}
            desc={t(steps[currentStep]?.descKey ?? "")}
            formData={formData}
            onCheckboxToggle={handleCheckboxToggle}
            onSetSelectedTowns={setSelectedTowns}
            selectedTowns={selectedTowns}
          />
        );
      case 2:
        return (
          <OnboardingVolunteerThirdStep
            title={t(steps[currentStep]?.titleKey ?? "")}
            desc={t(steps[currentStep]?.descKey ?? "")}
            formData={formData}
            onCheckboxToggle={handleCheckboxToggle}
          />
        );
      default:
        return null;
    }
  };

  const handleClickNext = async () => {
    if (!currentUser) return;

    const updatedData = {
      ...formData,
      // skills and interests are already strings, backend expects strings
      step_of_registration: currentStep + 1,
    };

    try {
      const data = await fetcher<any>({
        url: `/volunteer/${currentUser.userId}`,
        method: "PUT",
        body: JSON.stringify(updatedData),
      });

      setCurrentStep(data?.step_of_registration);
      data?.step_of_registration > 2 &&
        navigate("/volunteer", { state: { from: location.pathname } });
    } catch (error) {
      setError("onboarding.alerts.volFailed");
      console.error("Error creating new volunteer:", error);
    }
  };

  const isValid =
    currentStep === 1
      ? formData.skills.length >= 1 && formData.locations.length >= 1
      : currentStep === 2
        ? formData.interests.length >= 1
        : false;

  if (loading)
    return (
      <div className={styles.onboarding}>
        <CircularProgress />
      </div>
    );

  return (
    <div className={styles.onboarding}>
      <div className={styles.header}>
        {currentStep === 0 && (
          <Button variant="text" onClick={() => navigate("/register")}>
            <ArrowBackIosIcon />
            {t("common.back")}
          </Button>
        )}
        <div className={styles.navigation}>
          <span className={styles.highligted}>
            {t("onboarding.stepCounter", {
              current: currentStep + 1,
              total: steps.length,
            })}
          </span>
          <br />
          {t(steps[currentStep]?.titleKey ?? "")}
        </div>
      </div>
      {duplicateEmail ? (
        <DuplicateEmailNotice />
      ) : error ? (
        <div className={styles.content}>{t(error)}</div>
      ) : (
        renderStep()
      )}
      {!error && !duplicateEmail && currentStep > 0 && (
        <Button
          variant="contained"
          size="large"
          onClick={handleClickNext}
          disabled={!isValid}
        >
          {currentStep < 2 ? t("onboarding.continue") : t("onboarding.finish")}
        </Button>
      )}
    </div>
  );
};
